/**
 * BarometerIcon - displays a chart icon with a deficit count badge.
 * Rendered on the calls list for programs with LABOUR_BAROMETER capability.
 *
 * - Shows badge with total deficit profession count
 * - Loading: skeleton badge with pulse animation
 * - Error / no data: graceful hide (renders nothing)
 *
 * @see docs/specifications/2026-03-06-kfs-barometr-zawodow.md
 */
import React from "react";
import { BarometerResponse } from "../../../../types/grants";
import { getTotalDeficitCount } from "./barometerUtils";

interface BarometerIconProps {
  /** Barometer data (undefined while loading or on error) */
  barometerData: BarometerResponse | undefined;
  /** Whether data is currently being fetched */
  isLoading: boolean;
  /** Whether fetching resulted in an error */
  isError: boolean;
  /** Click handler to open the barometer modal */
  onClick?: () => void;
}

const BarometerIcon = React.forwardRef<HTMLButtonElement, BarometerIconProps>(({
  barometerData,
  isLoading,
  isError,
  onClick,
}, ref) => {
  // Error state: graceful hide
  if (isError) return null;

  // Loading state: skeleton badge
  if (isLoading) {
    return (
      <span
        data-testid="barometer-skeleton"
        className="inline-flex items-center gap-1 px-2 py-1 rounded animate-pulse bg-neutral-200"
        role="status"
        aria-busy="true"
        aria-label="Ładowanie barometru..."
      >
        <span className="w-4 h-4 bg-neutral-300 rounded" />
        <span className="w-4 h-3 bg-neutral-300 rounded" />
      </span>
    );
  }

  // No data or empty districts: hide
  if (!barometerData || barometerData.districts.length === 0) return null;

  const deficitCount = getTotalDeficitCount(barometerData.districts);

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      title="Barometr zawodów - zawody z deficytem"
      className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm text-neutral-700 hover:bg-neutral-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
    >
      {/* Chart bar icon */}
      <svg
        className="w-4 h-4 text-neutral-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      </svg>
      {/* Badge with deficit count */}
      <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 text-xs font-medium bg-neutral-200 text-neutral-800 rounded-full">
        {deficitCount}
      </span>
    </button>
  );
});

BarometerIcon.displayName = "BarometerIcon";

export default BarometerIcon;
